import { SDK } from "@ua/web-sdk"
import { PreferenceCenter } from "@ua/web-sdk/lib/remote-data/preference-center"

import { AsyncThunkOptions, createAsyncThunk } from "@reduxjs/toolkit"
import {
  AssociatedEmailChannel,
  AssociatedIdentifier,
  AssociatedOpenChannel,
  AssociatedSmsChannel,
  Attribute,
  ChannelInfo,
  CustomEvent,
  DebugInfo,
  FeatureFlagEligibilityCheck,
  LocaleOverride,
  PushStatus,
  SubscriptionListPayload,
  SupportInfo,
  Tag,
} from "./types"
import { ScopedSubscriptionListIdentifier } from "@ua/web-sdk/lib/types"
import { openDB } from "idb"
import { appKey } from "@root/globals"
import { PrivacyManagerFeature } from "@ua/web-sdk/lib/privacy-manager/types"
interface SDKThunkOptions extends AsyncThunkOptions {
  extra: { sdkPromise: Promise<SDK> }
}

/**
 * Debug
 */
export const loadDebugInfo = createAsyncThunk<DebugInfo, void, SDKThunkOptions>(
  "debug/loadDebugInfo",
  async (_, thunkApi) => {
    const sdk = await thunkApi.extra.sdkPromise

    const channelId = (await sdk.channel.id()) ?? undefined
    let pushStatus: PushStatus = "unregistered"
    let iaaDisplayPausedState = false
    if (channelId) {
      pushStatus = (await sdk.channel.optedIn()) ? "opted_in" : "opted_out"
      iaaDisplayPausedState = await sdk.components.inAppAutomation.isPaused()
    }

    // this is gross, but we need to prod the internal implementation for some
    // contact data; this is data we _never_ want to reveal to our customers so
    // the implementation is purposefully hidden, but sometimes we need to know
    // these things for QA purposes.
    const contact: any = sdk.contact
    let contactId: string = "unknown"
    let contactIsAnonymous: boolean = true

    // put this in a try block just in case we move around the sdk impl; we are
    // prodding internals that should absolutely be refactorable without
    // caring about upstream implementations
    try {
      contactId = await contact._storage.getOrCreateContactId()
      contactIsAnonymous = !Boolean(await contact._storage.getNamedUserId())
    } catch (e) {
      // log this to preserve the original stacktrace
      console.error("error fulfilling contact debug data", e)
      const message = e instanceof Error ? e.message : "unknown error"
      throw new Error(`unable to fulfill contact data: ${message}`)
    }

    let pushHistory: Record<string, unknown>[] = []
    try {
      const db = await openDB("ua_webpush_demo", 1, {
        upgrade(db) {
          db.createObjectStore("pushHistory", { autoIncrement: true })
        },
      })
      pushHistory = (await db.getAll("pushHistory")) ?? []
    } catch (e) {
      console.error("error getting most recent push data", e)
    }

    const info: DebugInfo = {
      channelId,
      pushStatus,
      supported: sdk.isSupported,
      notificationPermission:
        (await sdk.getNotificationPermission()) ?? "unsupported",
      contactId,
      contactIsAnonymous,
      pushHistory,
      iaaDisplayPausedState,
      privacyManagerFeatures: await sdk.privacyManager.listEnabled(),
    }

    return info
  }
)

export const toggleIaaDisplayPaused = createAsyncThunk<
  boolean,
  void,
  SDKThunkOptions
>("debug/toggleIaaDisplayPaused", async (_, thunkApi) => {
  const sdk = await thunkApi.extra.sdkPromise
  const isPaused = await sdk.components.inAppAutomation.isPaused()
  await sdk.components.inAppAutomation.setPaused(!isPaused)
  return !isPaused
})

export const enablePrivacyManagerFeatures = createAsyncThunk<
  PrivacyManagerFeature[],
  PrivacyManagerFeature[],
  SDKThunkOptions
>("debug/enablePrivacyManagerFeatures", async (features, thunkApi) => {
  const sdk = await thunkApi.extra.sdkPromise
  await sdk.privacyManager.enable(...features)
  return sdk.privacyManager.listEnabled()
})

export const disablePrivacyManagerFeatures = createAsyncThunk<
  PrivacyManagerFeature[],
  PrivacyManagerFeature[],
  SDKThunkOptions
>("debug/disablePrivacyManagerFeatures", async (features, thunkApi) => {
  const sdk = await thunkApi.extra.sdkPromise
  await sdk.privacyManager.disable(...features)
  return sdk.privacyManager.listEnabled()
})

export const loadPrivacyManagerFeatures = createAsyncThunk<
  PrivacyManagerFeature[],
  void,
  SDKThunkOptions
>("debug/loadPrivacyManagerFeatures", async (_, thunkApi) => {
  const sdk = await thunkApi.extra.sdkPromise
  return sdk.privacyManager.listEnabled()
})

/**
 * Metadata
 */
export const loadLocaleOverride = createAsyncThunk<
  LocaleOverride,
  void,
  SDKThunkOptions
>("metadata/loadLocaleOverride", async (_, thunkApi) => {
  const sdk = await thunkApi.extra.sdkPromise

  const { language, country } = await sdk.locale.get()

  return {
    language,
    country: country ?? undefined,
  }
})

export const setLocaleLanguageOverride = createAsyncThunk<
  string | undefined,
  string | undefined,
  SDKThunkOptions
>("metadata/setLocaleLanguageOverride", async (language, thunkApi) => {
  const sdk = await thunkApi.extra.sdkPromise

  await sdk.locale.set({ language: language || null })

  return language
})

export const setLocaleCountryOverride = createAsyncThunk<
  string | undefined,
  string | undefined,
  SDKThunkOptions
>("metadata/setLocaleCountryOverride", async (country, thunkApi) => {
  const sdk = await thunkApi.extra.sdkPromise

  await sdk.locale.set({ country: country || null })

  return country
})

export const loadPreferenceCenters = createAsyncThunk<
  PreferenceCenter[],
  void,
  SDKThunkOptions
>("metadata/loadPreferenceCenters", async (_, thunkApi) => {
  const sdk = await thunkApi.extra.sdkPromise
  return sdk.components.preferenceCenters.list()
})

export const loadSupportInfo = createAsyncThunk<
  SupportInfo,
  void,
  SDKThunkOptions
>("metadata/loadSupportInfo", async (_, thunkApi) => {
  const sdk = await thunkApi.extra.sdkPromise
  return {
    isSupported: sdk.isSupported,
    canRegister: await sdk.canRegister(),
    dataCollectionEnabled: await sdk.isDataCollectionEnabled(),
  }
})

export const getFeatureFlag = createAsyncThunk<
  FeatureFlagEligibilityCheck,
  string,
  SDKThunkOptions
>("metadata/getFeatureFlag", async (name, thunkApi) => {
  const sdk = await thunkApi.extra.sdkPromise
  const flag = await sdk.components.featureFlags.get(name)
  return { name, flag, timestamp: Date.now() }
})

export const trackScreen = createAsyncThunk<void, string, SDKThunkOptions>(
  "metadata/trackScreen",
  async (name, thunkApi) => {
    const sdk = await thunkApi.extra.sdkPromise
    await sdk.analytics.trackScreen(name)
  }
)

export const setDataCollectionEnabled = createAsyncThunk<
  boolean,
  boolean,
  SDKThunkOptions
>("metadata/setDataCollection", async (enabled, thunkApi) => {
  const sdk = await thunkApi.extra.sdkPromise
  await sdk.setDataCollectionEnabled(enabled)
  return enabled
})

/**
 * Channel
 */

export const addChannelTag = createAsyncThunk<Tag, Tag, SDKThunkOptions>(
  "channel/addTag",
  async (tag, thunkApi) => {
    const sdk = await thunkApi.extra.sdkPromise
    const channel = sdk.channel

    if (!channel) {
      throw new Error("no channel")
    }

    await channel.editTags().add(tag.group, tag.tag).apply()

    return tag
  }
)

export const removeChannelTag = createAsyncThunk<Tag, Tag, SDKThunkOptions>(
  "channel/removeTag",
  async (tag, thunkApi) => {
    const sdk = await thunkApi.extra.sdkPromise
    const channel = sdk.channel

    if (!channel) {
      throw new Error("no channel")
    }

    await channel.editTags().remove(tag.group, tag.tag).apply()

    return tag
  }
)

export const setChannelAttribute = createAsyncThunk<
  Attribute,
  Attribute,
  SDKThunkOptions
>("channel/setAttribute", async (attr, thunkApi) => {
  const sdk = await thunkApi.extra.sdkPromise
  const channel = sdk.channel

  if (!channel) {
    throw new Error("no channel")
  }

  await channel.editAttributes().set(attr.key, attr.value).apply()

  return attr
})

export const removeChannelAttribute = createAsyncThunk<
  Attribute,
  Attribute,
  SDKThunkOptions
>("channel/removeAttribute", async (attr, thunkApi) => {
  const sdk = await thunkApi.extra.sdkPromise
  const channel = sdk.channel

  if (!channel) {
    throw new Error("no channel")
  }

  await channel.editAttributes().remove(attr.key).apply()

  return attr
})

export const createChannel = createAsyncThunk<
  ChannelInfo,
  void,
  SDKThunkOptions
>("channel/create", async (_, thunkApi) => {
  const sdk = await thunkApi.extra.sdkPromise

  const { channelId } = await sdk.create()
  return {
    id: channelId,
    optedIn: await sdk.channel.optedIn(),
  }
})

export const registerChannel = createAsyncThunk<
  ChannelInfo,
  void,
  SDKThunkOptions
>("channel/register", async (_, thunkApi) => {
  const sdk = await thunkApi.extra.sdkPromise

  const { channelId } = await sdk.register()
  return {
    id: channelId,
    optedIn: await sdk.channel.optedIn(),
  }
})

export const optOutChannel = createAsyncThunk<boolean, void, SDKThunkOptions>(
  "channel/optOut",
  async (_, thunkApi) => {
    const sdk = await thunkApi.extra.sdkPromise

    await sdk.channel.optOut()
    return await sdk.channel.optedIn()
  }
)

export const loadChannel = createAsyncThunk<ChannelInfo, void, SDKThunkOptions>(
  "channel/load",
  async (_, thunkApi) => {
    const sdk = await thunkApi.extra.sdkPromise

    return {
      id: (await sdk.channel.id()) ?? undefined,
      optedIn: await sdk.channel.optedIn(),
    }
  }
)

export const loadChannelSubscriptions = createAsyncThunk<
  string[],
  void,
  SDKThunkOptions
>("channel/subscriptions", async (_, thunkApi) => {
  const sdk = await thunkApi.extra.sdkPromise

  return sdk.channel.subscriptions.list()
})

export const subscribeChannel = createAsyncThunk<
  string,
  string,
  SDKThunkOptions
>("channel/subscribe", async (listId, thunkApi) => {
  const sdk = await thunkApi.extra.sdkPromise

  if (!sdk.channel) {
    throw new Error("no channel")
  }

  await sdk.channel.subscriptions.edit().subscribe(listId).apply()

  return listId
})

export const unsubscribeChannel = createAsyncThunk<
  string,
  string,
  SDKThunkOptions
>("channel/unsubscribe", async (listId, thunkApi) => {
  const sdk = await thunkApi.extra.sdkPromise

  if (!sdk.channel) {
    throw new Error("no channel")
  }

  await sdk.channel.subscriptions.edit().unsubscribe(listId).apply()

  return listId
})

export const emitChannelCustomEvent = createAsyncThunk<
  void,
  CustomEvent,
  SDKThunkOptions
>("channel/emitCustomEvent", async (event, thunkApi) => {
  const sdk = await thunkApi.extra.sdkPromise

  if (!sdk.channel) {
    throw new Error("no channel")
  }

  const apiEvent = new sdk.CustomEvent(event.name, undefined, {})

  if (event.value) {
    apiEvent.value = event.value
  }
  if (event.transactionId) {
    apiEvent.transactionId = event.transactionId
  }
  if (event.interactionId) {
    apiEvent.interactionId = event.interactionId
  }
  if (event.interactionType) {
    apiEvent.interactionType = event.interactionType
  }

  event.properties.forEach((event) => {
    apiEvent.properties[event.key] = event.value
  })

  await apiEvent.track()
})

/**
 * Contact
 */

export const addContactTag = createAsyncThunk<Tag, Tag, SDKThunkOptions>(
  "contact/addTag",
  async (tag, thunkApi) => {
    const sdk = await thunkApi.extra.sdkPromise

    await sdk.contact.editTags().add(tag.group, tag.tag).apply()

    return tag
  }
)

export const removeContactTag = createAsyncThunk<Tag, Tag, SDKThunkOptions>(
  "contact/removeTag",
  async (tag, thunkApi) => {
    const sdk = await thunkApi.extra.sdkPromise

    await sdk.contact.editTags().remove(tag.group, tag.tag).apply()

    return tag
  }
)

export const setContactAttribute = createAsyncThunk<
  Attribute,
  Attribute,
  SDKThunkOptions
>("contact/setAttribute", async (attr, thunkApi) => {
  const sdk = await thunkApi.extra.sdkPromise

  await sdk.contact.editAttributes().set(attr.key, attr.value).apply()

  return attr
})

export const removeContactAttribute = createAsyncThunk<
  Attribute,
  Attribute,
  SDKThunkOptions
>("contact/removeAttribute", async (attr, thunkApi) => {
  const sdk = await thunkApi.extra.sdkPromise

  await sdk.contact.editAttributes().remove(attr.key).apply()

  return attr
})

export const loadContactSubscriptions = createAsyncThunk<
  ScopedSubscriptionListIdentifier,
  void,
  SDKThunkOptions
>("contact/subscriptions", async (_, thunkApi) => {
  const sdk = await thunkApi.extra.sdkPromise

  return sdk.contact.subscriptions.list()
})

export const subscribeContact = createAsyncThunk<
  SubscriptionListPayload,
  SubscriptionListPayload,
  SDKThunkOptions
>("contact/subscribe", async ({ listId, scope }, thunkApi) => {
  const sdk = await thunkApi.extra.sdkPromise

  await sdk.contact.subscriptions.edit().subscribe(listId, scope).apply()

  return { listId, scope }
})

export const unsubscribeContact = createAsyncThunk<
  SubscriptionListPayload,
  SubscriptionListPayload,
  SDKThunkOptions
>("contact/unsubscribe", async ({ listId, scope }, thunkApi) => {
  const sdk = await thunkApi.extra.sdkPromise

  await sdk.contact.subscriptions.edit().unsubscribe(listId, scope).apply()

  return { listId, scope }
})

export const identifyContact = createAsyncThunk<
  string,
  string,
  SDKThunkOptions
>("contact/identify", async (namedUserId, thunkApi) => {
  const sdk = await thunkApi.extra.sdkPromise

  await sdk.contact.identify(namedUserId)

  return namedUserId
})

export const resetContact = createAsyncThunk<void, void, SDKThunkOptions>(
  "contact/reset",
  async (_, thunkApi) => {
    const sdk = await thunkApi.extra.sdkPromise

    return sdk.contact.reset()
  }
)

export const associateEmail = createAsyncThunk<
  AssociatedEmailChannel,
  AssociatedEmailChannel,
  SDKThunkOptions
>("contact/associateEmail", async (email, thunkApi) => {
  const sdk = await thunkApi.extra.sdkPromise

  await sdk.contact.registerEmail(email.address, {
    commercialOptedIn: new Date(),
  })

  return email
})

export const associateSms = createAsyncThunk<
  AssociatedSmsChannel,
  AssociatedSmsChannel,
  SDKThunkOptions
>("contact/associateSms", async (sms, thunkApi) => {
  const sdk = await thunkApi.extra.sdkPromise

  await sdk.contact.registerSms(sms.msisdn, { senderId: sms.senderId })

  return sms
})

export const associateOpen = createAsyncThunk<
  AssociatedOpenChannel,
  AssociatedOpenChannel,
  SDKThunkOptions
>("contact/associateOpen", async (open, thunkApi) => {
  const sdk = await thunkApi.extra.sdkPromise

  await sdk.contact.registerOpen(open.address, {
    platformName: open.openPlatformName,
  })

  return open
})

export const clearEverything = createAsyncThunk<null, void, SDKThunkOptions>(
  "debug/clearEverything",
  async (_, thunkApi) => {
    const sdk = await thunkApi.extra.sdkPromise
    await sdk.channel?.optOut()
    const contact = await sdk.contact
    await contact.reset()

    // Here we're recreating the db name generation from the web sdk
    // This prefix is from the configs.
    const prefix = "_ua_sdk"
    const base = window.btoa(`${prefix}:${appKey}:/`)
    const pushWorker = window.btoa(`${prefix}:${appKey}:/push-worker.js`)

    await window.indexedDB.deleteDatabase(`${base}:db`)
    await window.indexedDB.deleteDatabase(`${pushWorker}:db`)
    await window.indexedDB.deleteDatabase("ua_webpush_demo")

    window.sessionStorage.clear()
    window.localStorage.clear()
    return null
  }
)

/**
 * Analytics
 */
export const getAssociatedIdentifiers = createAsyncThunk<
  AssociatedIdentifier[],
  void,
  SDKThunkOptions
>("analytics/getAssociatedIdentifiers", async (_, thunkApi) => {
  const sdk = await thunkApi.extra.sdkPromise
  return await sdk.analytics.associatedIdentifiers.list()
})

export const addAssociatedIdentifier = createAsyncThunk<
  AssociatedIdentifier,
  AssociatedIdentifier,
  SDKThunkOptions
>("analytics/addAssociatedIdentifier", async (identifier, thunkApi) => {
  const [key, value] = Object.entries(identifier)[0] || ["", ""]

  const sdk = await thunkApi.extra.sdkPromise
  await sdk.analytics.associatedIdentifiers.edit().add(key, value).apply()

  return identifier
})

export const removeAssociatedIdentifier = createAsyncThunk<
  string,
  string,
  SDKThunkOptions
>("analytics/removeAssociatedIdentifier", async (identifierKey, thunkApi) => {
  const sdk = await thunkApi.extra.sdkPromise
  await sdk.analytics.associatedIdentifiers.edit().remove(identifierKey).apply()
  return identifierKey
})
